import type { InputDrive } from "@townsy/contracts";
import { stepVehicle, wrapAngle, type DriveEnv, type DriveInput, type VehicleMotion } from "@townsy/world";
import { MAX_PENDING_INPUTS } from "./prediction";

/**
 * Client-side prediction for the car the local player drives (ADR-0006), the same pattern as
 * walking (prediction.ts): every tick runs the SAME `stepVehicle` the server runs, at the server's
 * fixed timestep, with an environment built the same way (drivable rects, static colliders, the
 * other cars, the other walkers). Each sent `input.drive` is kept with its sequence number; when
 * the car's state arrives with `lastSeq`, the prediction rewinds to the server pose and replays the
 * inputs the server has not applied yet.
 *
 * Corrections: a small disagreement (the other cars and walkers moved between the client's and
 * the server's step) is absorbed by a visual offset that decays over a few ticks, so the drawn car
 * never jumps; a large one (a teleport, a refused move, "Get out" braking) snaps.
 *
 * While "Get out" is pending the server brakes the car by itself: the predictor is HELD (no input
 * is applied or sent) and simply follows the server pose until the result arrives.
 *
 * Drawing (ADR-0049): every simulation step (held or not) records the pose to draw, and the renderer
 * puts each one on the local car's even 50 ms timeline (scene/town/localCarTimeline.ts), so it never
 * has to guess how many steps an update covers. A server state records nothing: the smoothed
 * correction keeps the drawn pose continuous; only a snap restarts the timeline.
 */
export interface VehicleAuthState {
  x: number;
  z: number;
  heading: number;
  speed: number;
  /** Last InputDrive the server applied (VehicleState.lastSeq). */
  lastSeq: number;
}

export interface PendingDrive {
  seq: number;
  input: DriveInput;
}

/** Beyond this position error (metres) a correction snaps instead of being smoothed. */
export const DRIVE_SNAP_DISTANCE = 3;
/** Beyond this heading error (radians) a correction snaps. */
export const DRIVE_SNAP_HEADING = 0.6;
/** The visual offset keeps this fraction per simulation step (~0.15 s to fade at 20 Hz). */
export const DRIVE_OFFSET_DECAY = 0.7;
const OFFSET_EPS = 1e-4;
const MAX_SEQ = 2_147_483_647;
/** Drawn steps kept until the renderer takes them (the movement loop runs at most 3 per callback). */
const MAX_DRAWN_STEPS = 8;

/** The steps run since the renderer last asked (oldest first), and whether its timeline must start over. */
export interface DrawnSteps {
  steps: VehicleMotion[];
  restart: boolean;
}

export interface DrivePredictorOptions {
  vehicleId: string;
  style: number;
  /** Server simulation rate (Hz); the fixed timestep is 1 / hz. */
  hz: number;
  initial: VehicleAuthState;
  /** First sequence number to use (strictly above anything this actor sent before in this room). */
  firstSeq: number;
  /** Environment for one step / one replay (the other cars and walkers as last received). */
  env(): DriveEnv;
}

function clampUnit(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return v < -1 ? -1 : v > 1 ? 1 : v;
}

export class DrivePredictor {
  readonly vehicleId: string;
  readonly style: number;
  readonly dt: number;
  state: VehicleMotion;
  private pending: PendingDrive[] = [];
  private nextSeq: number;
  private lastAck: number;
  private held = false;
  private offset = { x: 0, z: 0, heading: 0 };
  private drawn: VehicleMotion[] = [];
  private drawnRestart = false;
  private readonly env: () => DriveEnv;
  /** Distance between the predicted and the replayed pose at the last reconciliation. */
  lastCorrection = 0;
  /** Corrections that were too large to smooth (diagnostics). */
  snaps = 0;

  constructor(opts: DrivePredictorOptions) {
    this.vehicleId = opts.vehicleId;
    this.style = opts.style;
    this.dt = 1 / opts.hz;
    this.env = opts.env;
    this.state = { x: opts.initial.x, z: opts.initial.z, heading: opts.initial.heading, speed: opts.initial.speed };
    this.lastAck = opts.initial.lastSeq;
    this.nextSeq = Math.min(MAX_SEQ, Math.max(1, Math.floor(opts.firstSeq), opts.initial.lastSeq + 1));
  }

  get pendingCount(): number {
    return this.pending.length;
  }

  get acknowledgedSeq(): number {
    return this.lastAck;
  }

  /** The highest sequence number handed out so far (0 when none). */
  get lastSentSeq(): number {
    return this.nextSeq - 1;
  }

  get isHeld(): boolean {
    return this.held;
  }

  /** The pose to draw: the prediction plus what is left of the last smoothed correction. */
  get visual(): VehicleMotion {
    return {
      x: this.state.x + this.offset.x,
      z: this.state.z + this.offset.z,
      heading: wrapAngle(this.state.heading + this.offset.heading),
      speed: this.state.speed,
    };
  }

  /**
   * One fixed simulation step with the driver's intent. Returns the sequenced message to send, or
   * null while held ("Get out" pending: the server is braking the car).
   */
  applyInput(intent: DriveInput): InputDrive | null {
    this.decayOffset();
    if (this.held) {
      this.recordDrawn();
      return null;
    }
    const input: DriveInput = { throttle: clampUnit(intent.throttle), steer: clampUnit(intent.steer) };
    this.state = stepVehicle(this.state, this.style, input, this.dt, this.env());
    const seq = this.nextSeq;
    if (this.nextSeq < MAX_SEQ) this.nextSeq++;
    this.pending.push({ seq, input });
    if (this.pending.length > MAX_PENDING_INPUTS) this.pending.splice(0, this.pending.length - MAX_PENDING_INPUTS);
    this.recordDrawn();
    return { seq, throttle: input.throttle, steer: input.steer };
  }

  /**
   * The drawn pose of every simulation step since the last call (one per `applyInput`, held or not), and whether the
   * renderer's timeline must start over (a snapped correction). Taking them empties the list.
   */
  takeDrawnSteps(): DrawnSteps {
    const out = { steps: this.drawn, restart: this.drawnRestart };
    this.drawn = [];
    this.drawnRestart = false;
    return out;
  }

  /** Rewind to the server's pose and replay every input it has not applied (seq > lastSeq). */
  reconcile(server: VehicleAuthState): void {
    if (!Number.isFinite(server.lastSeq) || server.lastSeq < this.lastAck) return; // stale / out of order
    this.lastAck = server.lastSeq;
    this.pending = this.pending.filter((p) => p.seq > server.lastSeq);
    let s: VehicleMotion = { x: server.x, z: server.z, heading: server.heading, speed: server.speed };
    if (!this.held && this.pending.length > 0) {
      const env = this.env();
      for (const p of this.pending) s = stepVehicle(s, this.style, p.input, this.dt, env);
    }
    this.adopt(s);
  }

  /**
   * Hold (true) while "Get out" is pending: nothing is applied or sent and the pose follows the
   * server. Releasing (false, e.g. NO_EXIT_SPACE) resumes prediction from the current pose.
   */
  hold(on: boolean): void {
    this.held = on;
    if (on) this.pending = [];
  }

  /** Follow an authoritative pose without replay (held, or a hard reset). */
  follow(server: VehicleAuthState): void {
    if (Number.isFinite(server.lastSeq) && server.lastSeq > this.lastAck) this.lastAck = server.lastSeq;
    this.pending = this.pending.filter((p) => p.seq > this.lastAck);
    this.adopt({ x: server.x, z: server.z, heading: server.heading, speed: server.speed });
  }

  private adopt(next: VehicleMotion): void {
    const dx = this.state.x - next.x;
    const dz = this.state.z - next.z;
    const dh = wrapAngle(this.state.heading - next.heading);
    this.lastCorrection = Math.hypot(dx, dz);
    if (this.lastCorrection > DRIVE_SNAP_DISTANCE || Math.abs(dh) > DRIVE_SNAP_HEADING) {
      this.offset = { x: 0, z: 0, heading: 0 };
      this.snaps++;
      // Drawn at once: the renderer's timeline starts over at the next step.
      this.drawn = [];
      this.drawnRestart = true;
    } else {
      // Keep drawing where the car was; the offset fades over the next few ticks.
      this.offset = { x: this.offset.x + dx, z: this.offset.z + dz, heading: wrapAngle(this.offset.heading + dh) };
      const len = Math.hypot(this.offset.x, this.offset.z);
      if (len > DRIVE_SNAP_DISTANCE) {
        this.offset.x *= DRIVE_SNAP_DISTANCE / len;
        this.offset.z *= DRIVE_SNAP_DISTANCE / len;
      }
    }
    this.state = next;
  }

  private recordDrawn(): void {
    this.drawn.push(this.visual);
    if (this.drawn.length > MAX_DRAWN_STEPS) {
      // Nobody drew for a while (no scene): only the newest steps matter, drawn from a fresh start.
      this.drawn.splice(0, this.drawn.length - MAX_DRAWN_STEPS);
      this.drawnRestart = true;
    }
  }

  private decayOffset(): void {
    const o = this.offset;
    o.x *= DRIVE_OFFSET_DECAY;
    o.z *= DRIVE_OFFSET_DECAY;
    o.heading *= DRIVE_OFFSET_DECAY;
    if (Math.abs(o.x) < OFFSET_EPS && Math.abs(o.z) < OFFSET_EPS && Math.abs(o.heading) < OFFSET_EPS) this.offset = { x: 0, z: 0, heading: 0 };
  }
}
